<template>
  <div class="flex flex-col">
    <div class="flex flex-wrap items-center justify-between gap-3 mb-4">
      <p class="text-sm text-gray-600 flex-1 min-w-0">
        Audit trail for administrative operations on NIRA credentials.
      </p>
      <button type="button" class="btn shrink-0" :disabled="loading" @click="loadAuditLogs">
        <i class="fa-solid fa-rotate-right" :class="{ 'fa-spin': loading }"></i>
        <span>Refresh</span>
      </button>
    </div>

    <div class="relative min-h-[320px]">
      <div
        v-if="loading && !loaded"
        class="absolute inset-0 z-10 flex flex-col items-center justify-center rounded border border-gray-200 bg-white/90"
      >
        <i class="fa-solid fa-circle-notch fa-spin text-3xl text-primary"></i>
        <p class="mt-3 text-sm text-gray-500">Loading NIRA audit logs...</p>
      </div>

      <div :class="{ invisible: loading && !loaded }">
        <div class="bg-white rounded border border-gray-200 overflow-hidden">
          <div class="overflow-x-auto">
            <table class="w-full text-xs">
              <thead class="bg-gray-50 sticky top-0">
                <tr>
                  <th class="text-left py-3 px-4 font-semibold text-gray-700 border-b border-gray-200 w-16">#</th>
                  <th class="text-left py-3 px-4 font-semibold text-gray-700 border-b border-gray-200">Action</th>
                  <th class="text-left py-3 px-4 font-semibold text-gray-700 border-b border-gray-200">Details</th>
                  <th class="text-left py-3 px-4 font-semibold text-gray-700 border-b border-gray-200">Username</th>
                  <th class="text-left py-3 px-4 font-semibold text-gray-700 border-b border-gray-200">Client IP</th>
                  <th class="text-left py-3 px-4 font-semibold text-gray-700 border-b border-gray-200">Timestamp</th>
                  <th class="text-center py-3 px-4 font-semibold text-gray-700 border-b border-gray-200 w-28">View</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(row, idx) in paginatedItems"
                  :key="auditRowKey(row, idx)"
                  class="hover:bg-gray-50 transition"
                >
                  <td class="py-3 px-4 border-b border-gray-200">{{ rowNumber(idx) }}</td>
                  <td class="py-3 px-4 border-b border-gray-200">
                    <span class="px-2 py-0.5 rounded-full text-xs font-semibold" :class="actionBadgeClass(row)">
                      {{ rowField(row, "action") }}
                    </span>
                  </td>
                  <td class="py-3 px-4 border-b border-gray-200 max-w-[16rem]">
                    <p class="truncate" :title="rowField(row, 'details')">{{ rowField(row, "details") }}</p>
                  </td>
                  <td class="py-3 px-4 border-b border-gray-200 max-w-[12rem]">
                    <p class="truncate" :title="rowField(row, 'username')">{{ rowField(row, "username") }}</p>
                  </td>
                  <td class="py-3 px-4 border-b border-gray-200 whitespace-nowrap">{{ rowField(row, "clientIp") }}</td>
                  <td class="py-3 px-4 border-b border-gray-200 whitespace-nowrap">{{ formatAuditTime(row) }}</td>
                  <td class="py-3 px-4 border-b border-gray-200 text-center">
                    <button type="button" class="btn-sm-primary" @click="viewDetail(row)">
                      <i class="fa-solid fa-eye"></i>
                      <span>View</span>
                    </button>
                  </td>
                </tr>
                <tr v-if="loaded && !loading && auditLogs.length === 0">
                  <td colspan="7" class="py-8 px-4 text-center text-gray-500">
                    No audit logs found.
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <GatewayTablePagination
          :current-page="currentPage"
          :total-pages="totalPages"
          :total-elements="totalElements"
          :page-size="pageSize"
          :range-start="rangeStart"
          :range-end="rangeEnd"
          @previous="previous"
          @next="next"
          @update:page-size="onPageSizeUpdate"
        />
      </div>
    </div>

    <AppModal v-model="showDetail" title="Audit Log Details" size="lg" hide-footer>
      <div v-if="selectedLog" class="audit-detail">
        <p v-for="entry in detailEntries" :key="entry.key" class="detail-row">
          <span class="detail-label">{{ entry.label }}:</span>
          <span class="detail-value-wrap">{{ entry.value }}</span>
        </p>
      </div>
    </AppModal>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import AppModal from "@/components/ui/AppModal.vue";
import GatewayTablePagination from "../gateway/GatewayTablePagination.vue";
import { useClientPagination } from "../gateway/useClientPagination";
import { useToast, useUtilities } from "@/composables";
import { extractErrorMessage, fetchNiraCredentialsAudit } from "./niraApi";

const { errorToast } = useToast();
const { dateFormat } = useUtilities();

const auditLogs = ref<Record<string, unknown>[]>([]);
const loading = ref(false);
const loaded = ref(false);
const showDetail = ref(false);
const selectedLog = ref<Record<string, unknown> | null>(null);

const {
  currentPage,
  pageSize,
  totalElements,
  totalPages,
  paginatedItems,
  rangeStart,
  rangeEnd,
  previous,
  next,
  resetPage,
} = useClientPagination(auditLogs, 25);

const onPageSizeUpdate = (size: number) => {
  pageSize.value = size;
  resetPage();
};

const rowNumber = (idx: number) => (currentPage.value - 1) * pageSize.value + idx + 1;

const detailLabels: Record<string, string> = {
  id: "ID",
  action: "Action",
  details: "Details",
  username: "Username",
  clientIp: "Client IP",
  timestampEpochMillis: "Timestamp",
  createdAt: "Created at",
  created_at: "Created at",
  updatedAt: "Updated at",
  updated_at: "Updated at",
  message: "Message",
};

const detailEntries = computed(() => {
  if (!selectedLog.value || typeof selectedLog.value !== "object") return [];
  return Object.keys(selectedLog.value).map((key) => {
    let value = selectedLog.value?.[key];
    if (value == null || value === "") {
      value = "—";
    } else if (typeof value === "object") {
      try {
        value = JSON.stringify(value, null, 2);
      } catch {
        value = String(value);
      }
    } else {
      value = String(value);
    }
    return { key, label: detailLabels[key] || key, value: value as string };
  });
});

const rowField = (row: Record<string, unknown> | null, key: string) => {
  if (!row || typeof row !== "object") return "—";
  if (row[key] != null && row[key] !== "") return String(row[key]);
  if (key === "username") {
    if (row.username != null) return String(row.username);
    if (row.performed_by != null) return String(row.performed_by);
    if (row.user != null) return String(row.user);
  }
  if (key === "clientIp" && row.client_ip != null) return String(row.client_ip);
  if (key === "action" && row.operation != null) return String(row.operation);
  if (key === "details" && row.message != null) return String(row.message);
  return "—";
};

const actionBadgeClass = (row: Record<string, unknown>) => {
  const action = rowField(row, "action").toLowerCase();
  if (action.includes("add") || action.includes("create") || action.includes("set")) {
    return "bg-green-100 text-green-800";
  }
  if (action.includes("remove") || action.includes("delete")) return "bg-red-100 text-red-800";
  return "bg-gray-100 text-gray-600";
};

const formatAuditTime = (row: Record<string, unknown>) => {
  const raw =
    row.timestampEpochMillis != null ? row.timestampEpochMillis
      : row.timestamp != null ? row.timestamp
        : row.createdAt != null ? row.createdAt
          : row.created_at != null ? row.created_at
            : null;
  if (raw == null) return "—";
  if (typeof raw === "number") return dateFormat(new Date(raw).toISOString()) || String(raw);
  return dateFormat(String(raw)) || String(raw);
};

const auditRowKey = (row: Record<string, unknown>, idx: number) => {
  if (row.id != null) return String(row.id);
  return "nira-audit-" + idx;
};

const viewDetail = (row: Record<string, unknown>) => {
  selectedLog.value = row;
  showDetail.value = true;
};

const loadAuditLogs = async () => {
  loading.value = true;
  try {
    auditLogs.value = await fetchNiraCredentialsAudit();
    resetPage();
  } catch (error: unknown) {
    auditLogs.value = [];
    resetPage();
    errorToast("Error", extractErrorMessage(error, "Could not load NIRA audit logs"));
  } finally {
    loading.value = false;
    loaded.value = true;
  }
};

onMounted(() => {
  loadAuditLogs();
});

defineExpose({ refresh: loadAuditLogs });
</script>

<style scoped>
.audit-detail .detail-row {
  margin-bottom: 0.5rem;
  font-size: 14px;
}

.audit-detail .detail-label {
  font-weight: 600;
  margin-right: 0.5rem;
  min-width: 120px;
  display: inline-block;
  vertical-align: top;
}

.audit-detail .detail-value-wrap {
  word-break: break-all;
  white-space: pre-wrap;
}
</style>
